/**
 * The control that turns the animation off — three positions, in the settings
 * sheet, beside the theme picker.
 *
 * It is here because **an animation that cannot be turned off is not a fun
 * one**. `prefers-reduced-motion` is the right default and it is honoured
 * without anybody touching this; but it is set in an operating system's
 * accessibility panel, and somebody who simply wants a calmer app on a phone
 * they have never been in there on has no way to say so. This is that way.
 *
 * ## Why three positions and not a checkbox
 *
 * Because the other override has to exist too. On a device that already
 * reduces motion system-wide, a "Reduce motion" checkbox is a box that is
 * ticked and cannot be unticked into anything — the app was never going to
 * move either way. `Full motion` is what makes the control honest on that
 * device, and `Match my device` is what everybody starts on and what almost
 * everybody stays on. `src/lib/motion.ts` is the rule; this is the three
 * words it is offered in.
 *
 * The preference is written to the user row, so it follows somebody to their
 * laptop, and applied before the request goes out, so the app stops moving on
 * the click rather than on the round trip — the same bargain `ThemePicker`
 * makes, and if the write is refused this puts the old one back the same way.
 */

import { useCallback, useState } from 'react'
import { chooseMotion } from '../api'
import {
  MOTION_LABELS,
  MOTION_PREFERENCES,
  isMotionPreference,
  type MotionPreference,
} from '../lib/motion'
import { showMotion } from '../motion-document'
import { useMotionState } from '../motion-react'

export function MotionToggle({ preference }: { preference: MotionPreference }) {
  const [chosen, setChosen] = useState<MotionPreference>(preference)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  // Not to decide anything — the `<select>` shows the preference, not the
  // resolution — but so that `Match my device` can say which way the device
  // actually went, and re-render when somebody changes that setting while
  // this is on the screen.
  const state = useMotionState()

  const pick = useCallback(
    async (value: string) => {
      if (!isMotionPreference(value) || value === chosen) return
      const previous = chosen
      setChosen(value)
      setBusy(true)
      setFailure(null)
      showMotion(value)
      try {
        await chooseMotion(value)
      } catch (err) {
        setChosen(previous)
        showMotion(previous)
        setFailure(err instanceof Error ? err.message : String(err))
      } finally {
        setBusy(false)
      }
    },
    [chosen],
  )

  return (
    <span className="motion">
      <label className="motion__label" htmlFor="motion-toggle">
        Motion
      </label>
      <select
        id="motion-toggle"
        className="motion__select"
        value={chosen}
        disabled={busy}
        onChange={(event) => pick(event.target.value)}
      >
        {MOTION_PREFERENCES.map((option) => (
          <option key={option} value={option}>
            {MOTION_LABELS[option]}
            {option === 'system' ? ` · ${state === 'reduced' ? 'reduced' : 'full'}` : ''}
          </option>
        ))}
      </select>
      {failure && <span className="theme__note theme__note--bad">{failure}</span>}
    </span>
  )
}
